/**
 * Contract Field Data Helpers
 * Converts between user-friendly formats and backend JSON format
 */

// Field name to key mapping - defines structure of each field
export const FIELD_CONFIG = {
   'Confidential Information': {
      key: 'confidential',
      type: 'text', // single text field
      description: 'Enter the confidential information text',
   },
   Amendments: {
      key: 'amendments',
      type: 'text',
      description: 'Enter the amendments text',
   },
   'General Terms & Conditions': {
      key: 'general_condition',
      type: 'array', // array of items
      description: 'Enter each condition on a new line',
   },
   'Project Termination Terms': {
      key: 'project_termination_term',
      type: 'array',
      description: 'Enter each termination term on a new line',
   },
   'Payment Terms & Conditions': {
      key: 'payment_term_condition',
      type: 'array',
      description: 'Enter each payment term on a new line',
   },
   'Application Ownership': {
      key: 'app_owner',
      type: 'text',
      description: 'Enter the application ownership text',
   },
   Liability: {
      key: 'liability',
      type: 'text',
      description: 'Enter the liability text',
   },
   'Legal Jurisdiction': {
      key: 'legal_jury',
      type: 'text',
      description: 'Enter the legal jurisdiction text',
   },
};

/**
 * Convert backend JSON format to user-friendly text format
 * @param {object} data - Raw JSON data from backend
 * @param {string} fieldName - Field name
 * @returns {string} User-friendly text (items separated by newlines for arrays)
 */
export const convertDataToText = (data, fieldName) => {
   if (!data || typeof data !== 'object') return '';

   const config = FIELD_CONFIG[fieldName];
   if (!config) return JSON.stringify(data, null, 2);

   const value = data[config.key];

   if (config.type === 'array' && Array.isArray(value)) {
      return value.join('\n');
   }

   return value || '';
};

/**
 * Convert user-friendly text format to backend JSON format
 * @param {string} text - User-entered text (items separated by newlines for arrays)
 * @param {string} fieldName - Field name
 * @returns {object} Backend JSON format
 */
export const convertTextToData = (text, fieldName) => {
   const config = FIELD_CONFIG[fieldName];
   if (!config) return {};

   if (config.type === 'array') {
      // Split by newlines and filter empty lines
      const items = text
         .split('\n')
         .map((item) => item.trim())
         .filter((item) => item.length > 0);
      return { [config.key]: items };
   }

   // For text type, just return the trimmed string
   return { [config.key]: text.trim() };
};

/**
 * Validate field data
 * @param {string} text - User-entered text
 * @param {string} fieldName - Field name
 * @returns {object} { isValid: boolean, error: string }
 */
export const validateFieldData = (text, fieldName) => {
   const config = FIELD_CONFIG[fieldName];
   if (text?.length > 5000) {
      return {
         isValid: false,
         error: `${fieldName} is too long. Please enter a shorter description within 5000 characters.`,
      }
   }

   if (!config) {
      return { isValid: false, error: 'Invalid field name' };
   }

   const trimmedText = text.trim();

   if (config.type === 'array') {
      const items = trimmedText
         .split('\n')
         .map((item) => item.trim())
         .filter((item) => item.length > 0);

      if (items.length === 0) {
         return { isValid: false, error: 'At least one item is required' };
      }

      return { isValid: true, error: null };
   }

   // Text type validation
   if (trimmedText.length === 0) {
      return { isValid: false, error: 'This field is required' };
   }

   return { isValid: true, error: null };
};

/**
 * Field name → Formik field name mapping (for contract forms)
 */
const FIELD_TO_FORMIK = {
   'Confidential Information': 'confidential_information',
   Amendments: 'amendments',
   'General Terms & Conditions': 'general_terms_and_conditions',
   'Project Termination Terms': 'project_termination_terms',
   'Payment Terms & Conditions': 'payment_terms',
   'Application Ownership': 'application_ownership',
   Liability: 'liability',
   'Legal Jurisdiction': 'legal_jurisdiction',
};

/**
 * Extract pre-fill values from a contract type's field_values array.
 * Converts array-type fields to <ul><li>…</li></ul> HTML for CKEditor.
 *
 * @param {Array} fieldValues - Array of field_value objects from GET /contract-type/view/:id
 * @returns {object} Formik-keyed object with HTML string values for all 8 rich-text fields
 */

// ─── contractFieldHelpers LOG UTILITY ────────────────────────────────────────
const CFH_LOG = (scope, event, data) => {
   const ts = new Date().toISOString().slice(11, 23);
   console.log(`[${ts}][contractFieldHelpers::${scope}] ${event}`, data !== undefined ? data : '');
};
// ─────────────────────────────────────────────────────────────────────────────

export const extractPreFillValues = (fieldValues) => {
   CFH_LOG('extractPreFillValues', 'called', {
      isArray: Array.isArray(fieldValues),
      length: Array.isArray(fieldValues) ? fieldValues.length : 'N/A',
   });

   const result = {
      confidential_information: '',
      amendments: '',
      general_terms_and_conditions: '',
      project_termination_terms: '',
      payment_terms: '',
      application_ownership: '',
      liability: '',
      legal_jurisdiction: '',
   };

   if (!Array.isArray(fieldValues)) {
      CFH_LOG('extractPreFillValues', 'ABORT — fieldValues is not an array', typeof fieldValues);
      return result;
   }

   fieldValues.forEach((item, idx) => {
      CFH_LOG('extractPreFillValues', `item[${idx}]`, {
         field_name: item.field_name,
         rawData: item.data,
      });

      const config = FIELD_CONFIG[item.field_name];
      const formikKey = FIELD_TO_FORMIK[item.field_name];

      if (!config) {
         CFH_LOG(
            'extractPreFillValues',
            `item[${idx}] SKIP — no config for field_name`,
            item.field_name,
         );
         return;
      }
      if (!formikKey) {
         CFH_LOG(
            'extractPreFillValues',
            `item[${idx}] SKIP — no formikKey for field_name`,
            item.field_name,
         );
         return;
      }

      const rawData = item.data;
      if (!rawData) {
         CFH_LOG(
            'extractPreFillValues',
            `item[${idx}] SKIP — rawData is null/undefined`,
            item.field_name,
         );
         return;
      }

      CFH_LOG('extractPreFillValues', `item[${idx}] config`, {
         field_name: item.field_name,
         formikKey,
         configType: config.type,
         configKey: config.key,
      });

      if (config.type === 'array') {
         const items = rawData[config.key];
         CFH_LOG('extractPreFillValues', `item[${idx}] ARRAY — raw items`, {
            configKey: config.key,
            items,
            isArray: Array.isArray(items),
            length: Array.isArray(items) ? items.length : 'N/A',
         });

         if (Array.isArray(items) && items.length > 0) {
            const html = items.map((s) => `<p>${s}</p>`).join('');
            CFH_LOG('extractPreFillValues', `item[${idx}] ARRAY — built HTML`, {
               formikKey,
               itemCount: items.length,
               html,
            });
            result[formikKey] = html;
         } else {
            CFH_LOG(
               'extractPreFillValues',
               `item[${idx}] ARRAY — items empty or not array, skipping`,
               {
                  formikKey,
                  items,
               },
            );
         }
      } else {
         const textVal = rawData[config.key] || '';
         CFH_LOG('extractPreFillValues', `item[${idx}] TEXT`, {
            formikKey,
            value: textVal ? textVal.substring(0, 80) : '(empty)',
         });
         result[formikKey] = textVal;
      }
   });

   CFH_LOG(
      'extractPreFillValues',
      'FINAL RESULT',
      Object.fromEntries(
         Object.entries(result).map(([k, v]) => [
            k,
            v ? `[${v.length} chars] ${v.substring(0, 80)}` : '(empty)',
         ]),
      ),
   );

   return result;
};
